<template>
  <div class="min-h-screen bg-gray-50 font-body">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between flex-col md:flex-row gap-3">
          <div class="flex items-center space-x-4 space-x-reverse">
            <div class="relative">
              <img 
                src="@/assets/Logo.png" 
                alt="کبابی مارو" 
                class="navbar-logo w-12 h-12"
              />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-gray-800">مدیریت سفارشات</h1>
              <p class="text-sm text-gray-500">کبابی مارو</p>
            </div>
          </div>
          
          <div class="flex items-center space-x-3 space-x-reverse flex-wrap justify-center md:justify-end">
            <NetworkIndicator />
            <button 
              @click="initializeData"
              :disabled="initializing"
              class="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="initializing">در حال بارگذاری...</span>
              <span v-else>به‌روزرسانی داده‌ها</span>
            </button>
            <button 
              @click="resetDatabase"
              :disabled="initializing"
              class="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              بازنشانی پایگاه داده
            </button>
             <button 
               @click="goToAdminPanel"
               class="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
             >
               بازگشت به پنل
             </button>
             <button 
               @click="goToSectionTableManagement"
               class="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
             >
               تنظیمات
             </button>
             <button 
               @click="goToMenu"
               class="bg-brown-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brown-700 transition-colors"
             >
               مشاهده منو
             </button>
           </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="container mx-auto px-4 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-brown-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-20">
        <div class="text-red-600 text-lg mb-4">{{ error }}</div>
        <button 
          @click="loadSections"
          class="bg-brown-600 text-white px-6 py-2 rounded-lg hover:bg-brown-700 transition-colors"
        >
          تلاش مجدد
        </button>
      </div>

      <!-- Content -->
      <div v-else class="space-y-6">
        <!-- Section Tabs -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <div class="flex space-x-1 space-x-reverse p-1 overflow-x-auto whitespace-nowrap">
                         <!-- Pickup and Internal Orders Tab -->
            <button 
               @click="activeSection = pickupSection"
               :class="[
                 'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
                 activeSection?.id === pickupSection.id 
                   ? 'bg-blue-600 text-white' 
                   : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
               ]"
              class="shrink-0"
             >
               سفارشات تحویل و داخلی
               <span class="ml-2 text-xs opacity-75">
                 ({{ pickupSection.tables.length }})
               </span>
             </button>
            
            <!-- Regular Section Tabs -->
            <button 
              v-for="section in sections" 
              :key="section.id"
              @click="activeSection = section"
              :class="[
                'flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors',
                activeSection?.id === section.id 
                  ? 'bg-brown-600 text-white' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              ]"
              class="shrink-0"
            >
              {{ section.name }}
              <span class="ml-2 text-xs opacity-75">
                ({{ section.tables.length }})
              </span>
            </button>
          </div>
        </div>

        <!-- Tables Grid -->
        <div v-if="activeSection && activeSection.tables.length > 0" class="space-y-4">
          <div class="flex justify-between items-center flex-col md:flex-row gap-3">
            <h2 class="text-2xl font-semibold text-gray-800">
              {{ activeSection.name }}
            </h2>
          <div class="flex items-center space-x-3 space-x-reverse flex-wrap justify-center md:justify-end">
              <!-- Create New Pickup Order Button -->
              <button 
                v-if="activeSection.id === 'pickup'"
                @click="createNewPickupOrder"
                class="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                + سفارش تحویل جدید
              </button>
              
              <!-- Create Internal Order Button -->
              <button 
                @click="createInternalOrder"
                class="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                سفارش داخلی
              </button>
              <button 
                @click="syncAll"
                :disabled="!online || syncing"
                class="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {{ syncing ? 'در حال همگام‌سازی...' : 'همگام‌سازی' }}
              </button>
              <div class="text-sm text-gray-500">
                {{ activeSection.tables.length }} {{ activeSection.id === 'pickup' ? 'سفارش' : 'میز' }}
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                         <div 
               v-for="table in activeSection.tables" 
               :key="table.id"
               @click="openTableOrder(table)"
               class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group"
               :class="{
                'border-2 border-green-500': table.hasOpenOrder && table.orderStatus === 0,
                'border-2 border-blue-500': table.hasOpenOrder && table.orderStatus === 1,
                'border-2 border-orange-500': table.hasOpenOrder && table.orderStatus === 3,
                 'border border-gray-200': !table.hasOpenOrder
               }"

             >
              <div class="text-center">
                <div class="text-3xl mb-2 group-hover:scale-110 transition-transform">
                  🍽️
                </div>
                <h3 class="font-semibold text-gray-800 text-lg mb-1">
                  {{ table.name }}
                </h3>
                <p class="text-sm text-gray-500 mb-3">
                  {{ table.description }}
                </p>
                
                <!-- Status Indicator -->
                <div class="flex items-center justify-center space-x-2 space-x-reverse mb-3">
                  <div 
                    :class="[
                      'w-3 h-3 rounded-full',
                      table.hasOpenOrder && table.orderStatus === 0 ? 'bg-green-500' : '',
                      table.hasOpenOrder && table.orderStatus === 1 ? 'bg-blue-500' : '',
                      table.hasOpenOrder && table.orderStatus === 3 ? 'bg-orange-500' : '',
                      !table.hasOpenOrder ? 'bg-gray-300' : ''
                    ]"
                  ></div>
                  <span class="text-xs text-gray-600">
                    {{ table.hasOpenOrder && table.orderStatus === 0 ? 'فعال' : '' }}
                    {{ table.hasOpenOrder && table.orderStatus === 1 ? 'بسته' : '' }}
                    {{ table.hasOpenOrder && table.orderStatus === 3 ? 'پرداخت نشده' : '' }}
                    {{ !table.hasOpenOrder ? 'خالی' : '' }}
                  </span>
                </div>

                <!-- Capacity -->
                <div class="text-xs text-gray-500 mb-2">
                  ظرفیت: {{ table.capacity }} نفر
                </div>

                <!-- Open Order Total -->
                <div v-if="table.hasOpenOrder && table.openOrderTotal" 
                     :class="[
                       'text-sm font-medium',
                       table.orderStatus === 0 ? 'text-green-600' : '',
                       table.orderStatus === 1 ? 'text-blue-600' : '',
                       table.orderStatus === 3 ? 'text-orange-600' : ''
                     ]">
                  {{ formatPrice(table.openOrderTotal) }}
                </div>
              </div>
            </div>
          </div>
        </div>

                 <!-- No Tables Message -->
         <div v-else-if="activeSection && activeSection.tables.length === 0" class="text-center py-20">
           <div class="text-gray-500 text-lg mb-4">
             {{ activeSection.id === 'pickup' ? 'هیچ سفارش تحویلی یا داخلی وجود ندارد' : 'هیچ میزی در این بخش وجود ندارد' }}
           </div>
           <div v-if="activeSection.id === 'pickup'" class="text-sm text-gray-400 mb-4">
             برای ایجاد سفارش جدید، از دکمه‌های زیر استفاده کنید
           </div>
           <div v-if="activeSection.id === 'pickup'" class="mt-4 space-x-3 space-x-reverse">
             <button 
               @click="createNewPickupOrder"
               class="bg-blue-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
             >
               + سفارش تحویل جدید
             </button>
             <button 
               @click="createInternalOrder"
               class="bg-purple-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
             >
               + سفارش داخلی جدید
             </button>
           </div>
         </div>
      </div>
    </div>

    <!-- Order Modal -->
    <OrderModal 
      v-if="showOrderModal"
      :order="currentOrder"
      :table="selectedTable"
      @close="closeOrderModal"
      @order-updated="handleOrderUpdated"
    />

    <!-- Employee Selection Modal -->
    <div v-if="showEmployeeSelectionModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-96 max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-800">انتخاب کارمند</h3>
          <button 
            @click="closeEmployeeSelectionModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">کارمند:</label>
          <select 
            v-model="selectedEmployee"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">انتخاب کنید...</option>
            <option 
              v-for="employee in employees" 
              :key="employee.id" 
              :value="employee"
            >
              {{ employee.fullName }} - {{ employee.phoneNumber }}
            </option>
          </select>
        </div>
        
        <div class="flex space-x-3 space-x-reverse">
          <button 
            @click="closeEmployeeSelectionModal"
            class="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
          >
            انصراف
          </button>
          <button 
            @click="selectEmployeeForInternalOrder"
            class="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            ایجاد سفارش
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { sectionsAPI, employeesAPI } from '@/services/api'
import OrderModal from './OrderModal.vue'
import NetworkIndicator from './NetworkIndicator.vue'
import { useNetworkStatus } from '@/services/network'
import { syncService } from '@/services/syncService'
import { localOrders, localEmployees, localSections, localTables, dataInitializer } from '@/services/localDb'

export default {
  name: 'OrderManagement',
  components: {
    OrderModal,
    NetworkIndicator
  },
  setup() {
    const { online } = useNetworkStatus()
    const syncing = ref(false)
    const router = useRouter()
    const sections = ref([])
    const activeSection = ref(null)
    const loading = ref(true)
    const initializing = ref(false)
    const error = ref(null)
        const showOrderModal = ref(false)
    const currentOrder = ref(null)
    const selectedTable = ref(null)
    const showEmployeeSelectionModal = ref(false)
    const employees = ref([])
    const selectedEmployee = ref(null)

    // Pickup and internal orders section
    const pickupSection = ref({
      id: 'pickup',
      name: 'سفارشات تحویل و داخلی',
      tables: []
    })

        const initializeData = async () => {
      try {
        initializing.value = true
        error.value = null
        
        console.log('🔄 Initializing data from API...')
        const result = await dataInitializer.initializeFromAPI()
        
        console.log('✅ Data initialized:', result)
        alert(`داده‌ها با موفقیت به‌روزرسانی شد:\n- بخش‌ها: ${result.sections}\n- میزها: ${result.tables}\n- آیتم‌های منو: ${result.menuItems}\n- دسته‌بندی‌ها: ${result.categories}\n- کارمندان: ${result.employees}`)
        
        // Reload all data
        await loadSections()
      } catch (err) {
        console.error('❌ Error initializing data:', err)
        error.value = 'خطا در به‌روزرسانی داده‌ها: ' + err.message
        alert('خطا در به‌روزرسانی داده‌ها: ' + err.message)
      } finally {
        initializing.value = false
      }
    }

    const resetDatabase = async () => {
      if (!confirm('آیا از بازنشانی پایگاه داده محلی اطمینان دارید؟ این عمل تمام داده‌های محلی را پاک می‌کند.')) {
        return
      }
      
      try {
        initializing.value = true
        error.value = null
        
        console.log('🔄 Resetting database...')
        await dataInitializer.resetDatabase()
        
        alert('پایگاه داده با موفقیت بازنشانی شد. حالا می‌توانید داده‌ها را از سرور بارگذاری کنید.')
        
        // Reload all data
        await loadSections()
      } catch (err) {
        console.error('❌ Error resetting database:', err)
        error.value = 'خطا در بازنشانی پایگاه داده: ' + err.message
        alert('خطا در بازنشانی پایگاه داده: ' + err.message)
      } finally {
        initializing.value = false
      }
    }

        const loadSections = async () => {
      try {
        loading.value = true
        error.value = null
        
        // Try to load from local database first, fallback to API
        let sectionsData = []
        let tablesData = []
        
        try {
          // Load from local database
          const [localSectionsData, localTablesData] = await Promise.all([
            localSections.listActive(),
            localTables.listActive()
          ])
          
          if (localSectionsData.length > 0 && localTablesData.length > 0) {
            console.log('📱 Using local data for sections and tables')
            sectionsData = localSectionsData
            tablesData = localTablesData
          } else {
            throw new Error('No local data available')
          }
        } catch (localErr) {
          console.log('🌐 Loading from API...')
          // Fallback to API - sections should include tables
          const sectionsResponse = await sectionsAPI.getAll()
          sectionsData = sectionsResponse.data || []
          tablesData = [] // Tables should be included in sections data
        }
        
        // Process sections and tables
        const processedSections = sectionsData.map(section => {
          // If tables are included in section data, use them
          const sectionTables = section.tables || []
          
          // If we have separate tables data, filter by section
          const sectionTablesFromData = tablesData.filter(table => 
            (table.sectionId || table.serverId) === (section.serverId || section.id)
          )
          
          // Combine tables from section data and separate tables data
          const allTables = [...sectionTables, ...sectionTablesFromData]
          
          return {
            ...section,
            id: section.serverId || section.id,
            tables: allTables.map(table => ({
              ...table,
              id: table.serverId || table.id,
              hasOpenOrder: false,
              orderStatus: null,
              openOrderTotal: 0,
              order: null
            }))
          }
        })
        
        sections.value = processedSections
        
        // Load local orders for all tables to show order status
        await loadLocalOrdersForTables()
        
        // Load pickup orders
        await loadPickupOrders()
        // Load employees (API when online, else local cache)
        await loadEmployees()
        // Set active section to pickup orders by default
        activeSection.value = pickupSection.value
      } catch (err) {
        console.error('Error loading sections:', err)
        error.value = 'خطا در بارگذاری بخش‌ها'
      } finally {
        loading.value = false
      }
    }

    const loadEmployees = async () => {
      try {
        // Try local first, then API
        try {
          const localEmployeesData = await localEmployees.listActive()
          if (localEmployeesData.length > 0) {
            console.log('📱 Using local employees data')
            employees.value = localEmployeesData
            return
          }
        } catch (localErr) {
          console.log('No local employees data available')
        }
        
        // Fallback to API
        if (online.value) {
          const res = await employeesAPI.getAll()
          const list = res?.data || []
          await localEmployees.upsertMany(list)
          employees.value = await localEmployees.listActive()
        } else {
          console.log('Offline: no employees data available')
          employees.value = []
        }
      } catch (err) {
        console.error('Error loading employees:', err)
        employees.value = await localEmployees.listActive()
      }
    }

    const loadLocalOrdersForTables = async () => {
      try {
        // Load local orders for all regular tables
        for (const section of sections.value) {
          for (const table of section.tables) {
            const order = await localOrders.getByTableId(table.id)
            if (order) {
              table.hasOpenOrder = true
              table.orderStatus = order.status
              table.openOrderTotal = order.totalAmount
              table.order = order
            } else {
              table.hasOpenOrder = false
              table.orderStatus = null
              table.openOrderTotal = 0
              table.order = null
            }
          }
        }
      } catch (err) {
        console.error('Error loading local orders for tables:', err)
      }
    }

    const loadPickupOrders = async () => {
      try {
        // Offline-first: read local open pickup/internal orders
        const local = await localOrders.listOpenPickupAndInternal()
        pickupSection.value.tables = local.map(order => ({
          id: `${order.isPickup ? 'pickup' : 'internal'}-${order.id}`,
          name: order.isPickup ? `سفارش تحویل #${order.id}` : `سفارش داخلی #${order.id}`,
          description: order.isPickup ? (order.notes || 'سفارش تحویل') : `کارمند: ${order.employeeId || 'نامشخص'}`,
          capacity: 1,
          hasOpenOrder: true,
          orderStatus: order.status,
          openOrderTotal: order.totalAmount,
          order
        }))
      } catch (err) {
        console.error('Error loading local pickup orders:', err)
      }
    }

    const createNewPickupOrder = async () => {
      try {
        // Offline: create a local pickup order
        const newOrder = await localOrders.createPickupOrder()
        await loadPickupOrders()
        currentOrder.value = newOrder
        selectedTable.value = {
          id: `pickup-${newOrder.id}`,
          name: `سفارش تحویل #${newOrder.id}`,
          description: 'سفارش تحویل',
          capacity: 1,
          hasOpenOrder: true,
          orderStatus: newOrder.status,
          openOrderTotal: newOrder.totalAmount,
          order: newOrder
        }
        showOrderModal.value = true
      } catch (err) {
        console.error('Error creating local pickup order:', err)
        alert('خطا در ایجاد سفارش تحویل (آفلاین)')
      }
    }

    const createInternalOrder = async () => {
      try {
        // Show employee selection modal first
        showEmployeeSelectionModal.value = true
      } catch (err) {
        console.error('Error creating internal order:', err)
        alert('خطا در ایجاد سفارش داخلی')
      }
    }

    const selectEmployeeForInternalOrder = async () => {
      try {
        if (!selectedEmployee.value) {
          alert('لطفاً یک کارمند انتخاب کنید')
          return
        }

        // Store employee info before clearing
        const employeeName = selectedEmployee.value.fullName
        const employeeId = selectedEmployee.value.id

        // Offline: create a new internal order locally
        const newOrder = await localOrders.createInternalOrder(employeeId, `سفارش داخلی - ${employeeName}`)
        
        // Close employee selection modal
        showEmployeeSelectionModal.value = false
        selectedEmployee.value = null
        
        currentOrder.value = newOrder
        selectedTable.value = {
          id: `internal-${newOrder.id}`,
          name: `سفارش داخلی #${newOrder.id}`,
          description: `کارمند: ${employeeName}`,
          capacity: 1,
          hasOpenOrder: true,
          orderStatus: newOrder.status,
          openOrderTotal: newOrder.totalAmount,
          order: newOrder
        }
        showOrderModal.value = true
      } catch (err) {
        console.error('Error creating internal order:', err)
        alert('خطا در ایجاد سفارش داخلی')
      }
    }

    const closeEmployeeSelectionModal = () => {
      showEmployeeSelectionModal.value = false
      selectedEmployee.value = null
    }

    const openTableOrder = async (table) => {
      try {
        selectedTable.value = table
        
        // For pickup/internal virtual tables use local
        if (String(table.id).startsWith('pickup-') || String(table.id).startsWith('internal-')) {
          const orderId = Number(String(table.id).split('-')[1])
          const order = await localOrders.getById(orderId)
          if (!order) {
            alert('سفارش محلی یافت نشد')
            return
          }
          currentOrder.value = order
        } else {
          // Regular tables - check for existing local order first
          const existingOrder = await localOrders.getByTableId(table.id)
          if (existingOrder) {
            currentOrder.value = existingOrder
          } else {
            // Create new local order for the table
            const newOrder = await localOrders.createTableOrder(table.id, table.name)
            currentOrder.value = newOrder
          }
        }

        showOrderModal.value = true
      } catch (err) {
        console.error('Error opening table order:', err)
        alert('خطا در باز کردن سفارش')
      }
    }

    const closeOrderModal = () => {
      showOrderModal.value = false
      currentOrder.value = null
      selectedTable.value = null
    }

         const handleOrderUpdated = (updatedOrder) => {
       // Update the current order with the new data if provided
       if (updatedOrder) {
         currentOrder.value = updatedOrder
       }
       
       // Refresh local orders for all tables
       loadLocalOrdersForTables()
       
       // If pickup section is active, refresh pickup and internal orders specifically
       if (activeSection.value?.id === 'pickup') {
         loadPickupOrders()
       }
     }

    const goToAdminPanel = () => {
      router.push('/admin')
    }

    const goToMenu = () => {
      router.push('/')
    }

    const goToSectionTableManagement = () => {
      router.push('/admin/sections-tables')
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fa-IR').format(price) + ' تومان'
    }

    const syncAll = async () => {
      try {
        syncing.value = true
        
        // Get sync status before sync
        const statusBefore = await syncService.getLocalSyncStatus()
        console.log('📊 Sync status before:', statusBefore)
        
        if (statusBefore.pendingUpload.total === 0) {
          alert('هیچ داده‌ای برای همگام‌سازی وجود ندارد')
          return
        }
        
        // Perform sync
        const result = await syncService.syncAll()
        console.log('✅ Sync result:', result)
        
        // Get sync status after sync
        const statusAfter = await syncService.getLocalSyncStatus()
        console.log('📊 Sync status after:', statusAfter)
        
        // Show detailed success message
        const message = `همگام‌سازی با موفقیت انجام شد:\n` +
          `- درج شده: ${result.stats?.inserted || 0}\n` +
          `- به‌روزرسانی شده: ${result.stats?.updated || 0}\n` +
          `- نگهداری محلی: ${result.stats?.keptLocal || 0}\n` +
          `- آخرین همگام‌سازی: ${statusAfter.lastSyncAt ? new Date(statusAfter.lastSyncAt).toLocaleString('fa-IR') : 'نامشخص'}`
        
        alert(message)
        
        // Refresh data after sync
        await loadSections()
      } catch (err) {
        console.error('❌ Sync failed:', err)
        alert('خطا در همگام‌سازی: ' + (err.message || 'خطای نامشخص'))
      } finally {
        syncing.value = false
      }
    }

    onMounted(() => {
      loadSections()
    })

    return {
      sections,
      activeSection,
      loading,
      initializing,
      error,
      showOrderModal,
      currentOrder,
      selectedTable,
      pickupSection,
      showEmployeeSelectionModal,
      employees,
      selectedEmployee,
      initializeData,
      resetDatabase,
      loadSections,
      openTableOrder,
      closeOrderModal,
      handleOrderUpdated,
      createNewPickupOrder,
      createInternalOrder,
      selectEmployeeForInternalOrder,
      closeEmployeeSelectionModal,
      goToAdminPanel,
      goToMenu,
      goToSectionTableManagement,
      formatPrice
      ,online, syncing, syncAll
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
